import { z } from 'zod';
import { entitlementSchema } from './licenses';

/** Configuration snapshot attached to a license request (drives PENDING license on approve). */
export const licenseRequestConfigurationSchema = z.object({
  type: z.enum(['perpetual', 'subscription', 'trial', 'dev']).optional(),
  maxActivations: z.number().int().optional(),
  expiresAt: z.string().nullable().optional(),
  updatesUntil: z.string().nullable().optional(),
  entitlements: z.array(entitlementSchema).optional(),
  options: z.record(z.unknown()).optional(),
});

export const licenseRequestStatusSchema = z.enum(['submitted', 'approved', 'rejected']);

export const licenseRequestSchema = z.object({
  id: z.string(),
  serial: z.string(),
  buyerCompany: z.string(),
  configuration: licenseRequestConfigurationSchema,
  status: licenseRequestStatusSchema,
  /** Operator id — null for machine submissions (CRM service token). */
  submittedBy: z.string().nullable(),
  /** Integration identifier for machine submissions (e.g. 'crm'); null for humans. */
  submittedByIntegration: z.string().nullable(),
  reviewedBy: z.string().nullable(),
  createdAt: z.string(),
  reviewedAt: z.string().nullable(),
  resultingLicenseId: z.string().nullable(),
  rejectionReason: z.string().nullable(),
  offlineRequested: z.boolean(),
  offlineRequestedAt: z.string().nullable(),
});

export const licenseRequestsListResponseSchema = z.object({
  data: z.array(licenseRequestSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  pages: z.number().int(),
});

export const approveRequestResponseSchema = z.object({
  ok: z.literal(true),
  request: licenseRequestSchema,
  license: z.object({
    id: z.string(),
    serial: z.string(),
    type: z.string(),
    status: z.string(),
  }),
});

export const rejectRequestResponseSchema = z.object({
  ok: z.literal(true),
  request: licenseRequestSchema,
});

export type LicenseRequestConfiguration = z.infer<typeof licenseRequestConfigurationSchema>;
export type LicenseRequestStatus = z.infer<typeof licenseRequestStatusSchema>;
export type LicenseRequest = z.infer<typeof licenseRequestSchema>;
export type LicenseRequestsListResponse = z.infer<typeof licenseRequestsListResponseSchema>;
export type ApproveRequestResponse = z.infer<typeof approveRequestResponseSchema>;
